// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */


import {productData, downloadLocaleMap} from '../constants/ccdConstants';
import getConfigApps from '../config';
import axios from 'axios';
import AppsUtil from './AppsUtil';
import {imageDraft} from './imageDraft';
import {getLocalizedURL} from '@intl/adobe-locales';
import Url from './Url';
import {
	LOGGED_OUT_CAMPAIGN_WORKFLOWS,
	LOGGED_OUT_CAMPAIGN_ANALYTICS
} from '../constants/AppPicker/appPickerConstants';

class CCDUtil {
	DTMEventData: ?Object;
	filterProductData: Object;
	downloadCC: ?Function;

	static DTMEventData: Object;

	constructor() {
		this.DTMEventData = null;
	}

	getProductIconURl(productId: string) {
		return AppsUtil.getProductIcon(AppsUtil.getFFCProductConfig(productId));
	}

	getCustomerSupportURL() {
		return 'http://www.adobe.com/go/cc_customersupport';
	}

	getLightroomLearnMoreURL(locale: string) {
		return getLocalizedURL(`${getConfigApps('servers').Adobe}/products/photoshop-lightroom-classic/lightroom-cc-vs-lightroom-classic.html`, locale);
	}

	isRouteToPathCampaign() {
		return AppsUtil.isLoggedOutCampaignExp
			&& Url.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.ROUTE_TO_PATH
			&& AppsUtil.getLoggedOutExpPathData().product === 'creative-cloud';
	}

	isProductRouteToPathCampaign(sapCode) {
		const supportedProducts = ['PHSP'];
		return AppsUtil.isLoggedOutCampaignExp
			&& Url.getParam('workflow') === LOGGED_OUT_CAMPAIGN_WORKFLOWS.ROUTE_TO_PATH
			&& AppsUtil.getLoggedOutExpPathData().product !== 'creative-cloud'
			&& supportedProducts.includes(sapCode);
	}

	isSEOCCDownloadPage() {
		return AppsUtil.isLoggedOutExp()
			&& !AppsUtil.isLoggedOutCampaignExp
			&& AppsUtil.getLoggedOutExpPathData().product === 'creative-cloud';
	}

	getCampaignWorkflowSubcategory() {
		const workflow = Url.getParam('workflow');
		return AppsUtil.isLoggedOutCampaignExp
			&& workflow
			? LOGGED_OUT_CAMPAIGN_ANALYTICS.WORKFLOW_TO_SUBCATEGORY[workflow]
			: 'Download';
	}

	/* supports 2x resolution or better */
	supportsRetinaDisplay() {
		return window.devicePixelRatio > 1;
	}

	getDisabledProductArray() {
		const disabledProducts = [];

		if (!AppsUtil.hasFeature('temp_onboard_aero_desktop_loggedout')) {
			disabledProducts.push('aero');
		}
		if (AppsUtil.hasFeature('temp_eol_fuse_loggedout')) {
			disabledProducts.push('fuse');
		}
		if (!AppsUtil.hasFeature('temp_cch_11998')) {
			disabledProducts.push('photoshop-express-desktop');
		}
		if (!AppsUtil.hasFeature('temp_cch_12206')) {
			disabledProducts.push('uxp-developer-tools');
		}
		if (!AppsUtil.hasFeature('temp_cch_8676')) {
			disabledProducts.push('substance3d-stager', 'substance-alchemist', 'substance3d-painter', 'substance3d-designer');
		}
		return disabledProducts;
	}

	getFilterProductData() {
		const disabledProducts = this.getDisabledProductArray();
		const data = {...productData};
		disabledProducts.forEach((product) => {
			if (data[product]) {
				delete data[product];
			}
		});
		this.filterProductData = data;
		return data;
	}

	getProductsData(productId: string) {
		const data = this.getFilterProductData();
		if (AppsUtil.hasFeature('temp_ccwt_6561')) {
			Object.keys(data).forEach((appId) => {
				data[appId].name = AppsUtil.getProductDisplayName(appId);
			});
		}
		return data[productId];
	}

	postData(url: string, data: string, headers: Object, timeout?: number) {
		const instance = axios.create();

		if (timeout) {
			instance.defaults.timeout = timeout;
		}

		return instance.post(
			url,
			data,
			{headers}
		);
	}

	// check isPlan() and has planId
	supportedForPlan(productId: string) {
		return productData[productId].type === 'application-plan' && productData[productId].planId !== null;
	}

	// return path (according to flag) if image is behind the flag
	getUrlPath(path: string) {
		let newPath;
		const self = this;
		imageDraft.some((draft) => {
			const flagActive = draft.featureFlags.some(flag => AppsUtil.hasFeature(flag));

			if (flagActive) {// If any of the feature flag for this entry is enabled then check if the path is present
				const pathArray = [
					{paths: draft.pathMap.files, isDir: false},
					{paths: draft.pathMap.dirs, isDir: true}
				];
				pathArray.some(asset => newPath = self.getNewPath(path,asset));
			}
			// If none of the feature flag for this entry is enabled then return false
			return false;
		});

		return newPath ? newPath : path;
	}

	// Usage in above function getUrlPath
	getNewPath(path: string, pathsObj: Object) {
		let xPath;

		if (pathsObj.paths) {
			const pathObj = pathsObj.paths.find(pathObj => path.indexOf(Object.keys(pathObj)[0]) === 0);

			if (pathObj) {
				const pathKey = Object.keys(pathObj)[0];
				const pathValue = pathObj[pathKey];

				if (pathsObj.isDir) {
					xPath = path.replace(pathKey, `${pathKey}/${pathValue}`);
				}
				else {
					const filename = path.split('/').pop();
					xPath = path.replace(filename, `${pathValue}/${filename}`);
				}
			}
		}
		return xPath;
	}

	setDTMEventData(product: string, subscriptionType: string, productId: string, loc: string) {
		const locale = downloadLocaleMap[loc];
		this.DTMEventData = {
			product,
			subscriptionType,
			productId,
			locale
		};
	}

	getDTMEventData() {
		const data = {...this.DTMEventData};
		return data;
	}

	hideFooter() {
		const footerElm = document.querySelector('.footer-container, .trailhead-footer-container');
		if (footerElm && footerElm.style) {
			footerElm.style.display = 'none';
		}
	}

	getCloneObject(data: Object) {
		return {...data};
	}
}

export default new CCDUtil();
